import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown, faCircleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { useContext } from 'react';
import { AccordionDetails } from '@mui/material';
import { QuestionInterface } from '../../types';
import ProcedureContext from '../../context/ProcedureContext';
import { ProcedureRecords } from '../../context/types';
import { ProcedureDisplayType } from '../../enums';
import { AlertDetailsList } from './AlertDetailsList';
import { prependVar } from '../../util/logic/ProcedureLogicUtil';

const AlertAccordion = styled(Accordion)`
  max-height: 384px;           /* Max height as specified by ux */
  overflow-y: auto;            /* Enables scrolling only when needed */
  display: block;              /* Ensures proper box behavior */
  box-shadow: none!important;
  background-color: ${(props) => props.theme.palette.grey[100]}!important;
  margin: 10px 0!important;
  font-size: ${(props) => props.theme.typography.fontSizes.sm};

  ::before {
    display: none;
  }

  .MuiAccordionSummary-root {
    align-items: flex-start;
    padding: 14px 27px 12px 17px;

    .MuiAccordionSummary-content {
      margin: 0!important;
    }
  }


  .MuiAccordionDetails-root {
    padding-top: 0;
    padding-bottom: 12px;
  }
`;

const AlertAccordionSummary = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  grid-gap: 0px 10px;
  margin: 0!important;
`;

const AlertAccordionSummaryIcon = styled(FontAwesomeIcon)`
  color: ${(props) => props.theme.palette.secondary.main};
  height: 1rem;
`;

const AlertAccordianSummaryHeader = styled.p`
  margin: 0;
  font-weight: ${(props) => props.theme.typography.fontWeightMedium};
`;

const AlertAccordianSummarySubheader = styled.p`
  grid-column-start: 2;
  margin: 0;
  color: ${(props) => props.theme.palette.grey[700]};
  font-size: ${(props) => props.theme.typography.fontSizes.xs};
`;

/**
 * Build a map from variableName → questionText.
 */
function getQuestionTextMap(questions: QuestionInterface[]): Record<string, string> {
  return Object.fromEntries(
    questions
      .filter(({ variableName }) => Boolean(variableName))
      .map(({ variableName, questionText }) => [prependVar(variableName!), questionText]),
  );
}

/**
 * Assign sequential indices (as strings) to each record that has ≥1 question.
 */
function getRecordIdIndices(records: ProcedureRecords): Record<string, string> {
  return Object.fromEntries(
    Object.entries(records)
      .filter(([, entry]) => entry?.record && Object.keys(entry.record).length > 0)
      .map(([recordId], idx) => [recordId, String(idx)]),
  );
}

export function SingleRecordView() {
  const { notificationAlerts: alertList, questions } = useContext(ProcedureContext);
  const questionTextMap = getQuestionTextMap(questions);
  const alerts = alertList[0]?.alerts ?? [];

  return (
    <AccordionDetails>
      <AlertDetailsList
        alerts={alerts}
        questionTextMap={questionTextMap}
        // no recordLabel in single
      />
    </AccordionDetails>
  );
}

export function MultiRecordView() {
  const { notificationAlerts: alertList, records, questions } = useContext(ProcedureContext);
  const questionTextMap = getQuestionTextMap(questions);
  const recordIdToIndex = getRecordIdIndices(records);
  const { t } = useTranslation();

  // TODO : Find more efficient way to do this (make this a part of the diffing or something so as to not incur extra runtime cost)
  const sortedAlertList = alertList.sort(
    (a, b) => Number(recordIdToIndex[a.recordId]) - Number(recordIdToIndex[b.recordId]),
  );

  return (
    <AccordionDetails>
      {sortedAlertList.map(({ recordId, alerts }) => (
        <AlertDetailsList
          key={recordId}
          recordLabel={`${t('Procedure.MultiRecord.Record')} #${recordIdToIndex[recordId]}`} // ← here
          alerts={alerts}
          questionTextMap={questionTextMap}
        />
      ))}
    </AccordionDetails>
  );
}
export default function AlertDropdown() {
  const { t } = useTranslation();
  const {
    notificationAlerts: alertList, displayType,
  } = useContext(ProcedureContext);

  const isMultiRecord = ProcedureDisplayType.MULTI_RECORD === displayType;

  if (alertList.length === 0) {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <></>;
  }

  // Calculate the number of Questions that triggered the Alert
  const triggeringQuestionsCount: number = alertList
    .map((alertListItem) => alertListItem.alerts.length)
    .reduce((sum, count) => sum + count, 0);

  return (
    <div>
      <AlertAccordion data-testid="procedure-rule-alerts">
        <AccordionSummary expandIcon={<FontAwesomeIcon icon={faCaretDown} />}>
          <AlertAccordionSummary>
            <AlertAccordionSummaryIcon icon={faCircleExclamation} />
            <AlertAccordianSummaryHeader>
              {triggeringQuestionsCount}
              {' '}
              {t('Procedure.Alerts.alert(s) found')}
            </AlertAccordianSummaryHeader>
            <AlertAccordianSummarySubheader>{t('Procedure.Alerts.Please review and make any necessary updates.')}</AlertAccordianSummarySubheader>
          </AlertAccordionSummary>
        </AccordionSummary>
        {isMultiRecord ? <MultiRecordView /> : <SingleRecordView /> }
      </AlertAccordion>
    </div>
  );
}
